import { ref, computed, watchEffect, unref } from "vue";
import {
  FACILITY_DEVICES,
  FACILITY_RENTERS,
  ADD_FACILITY_DEVICE_RENTER,
  CREATE_FACILITY_RENTER,
  REMOVE_FACILITY_DEVICE_RENTER,
} from "./graphql";
import { apolloWatchQuery } from "../../apollo-client/helper";
import useFacility from "../authentication/useFacility";

const apolloClient = ref(null);
let observableQuery = null;
function setApolloClient(apolloProvider) {
  observableQuery = null;
  apolloClient.value = apolloProvider.clients.apolloXpmMvpClient;
}

const devices = ref([]);
const facilityDevicesLoading = ref(false);

const devicesWithRenters = computed(() => {
  return devices.value?.filter((device) => device.renter) || [];
});

const devicesWithoutRenters = computed(() => {
  return devices.value?.filter((device) => !device.renter) || [];
});

export function useXpmInventory() {
  const { currentFacility } = useFacility();

  function fetchFacilityDevices() {
    if (!apolloClient.value) {
      console.error("XPM Apollo Client is not set");
      return;
    }
    if (!currentFacility.value?.code) {
      console.error("Unknown current facility code");
      return;
    }

    facilityDevicesLoading.value = true;
    devices.value = [];

    return apolloWatchQuery(
      apolloClient.value,
      {
        query: FACILITY_DEVICES,
        variables: { facilityCode: currentFacility.value?.code },
        fetchPolicy: "cache-and-network",
      },
      (data, loading) => {
        devices.value = data?.facilityDevices;
        facilityDevicesLoading.value = loading;
      }
    );
  }
  watchEffect(() => {
    if (currentFacility.value && apolloClient.value) {
      observableQuery = fetchFacilityDevices();
    }
  });

  async function fetchFacilityRenters() {
    if (!apolloClient.value || !currentFacility.value?.code) {
      console.error("XPM Apollo Client is not set");
      return;
    }
    try {
      const result = await apolloClient.value.query({
        query: FACILITY_RENTERS,
        variables: { facilityCode: currentFacility.value?.code },
        fetchPolicy: "network-only",
      });
      return result.data?.facilityRenters;
    } catch (error) {
      console.error("Error fetching facility devices:", error);
    }
  }

  async function addRenterDevice(deviceRef, renterRef) {
    const device = unref(deviceRef);
    const renter = unref(renterRef);
    if (
      !apolloClient.value ||
      !currentFacility.value?.code ||
      !device?.id ||
      !renter?.id
    )
      return;

    try {
      const result = await apolloClient.value.mutate({
        mutation: ADD_FACILITY_DEVICE_RENTER,
        variables: {
          deviceId: device.id,
          renterId: renter.id,
          facilityCode: currentFacility.value?.code,
        },
      });
      // No need to refetch, the cache will be updated automatically
      return {
        result: result.data?.addFacilityDeviceRenter,
        error: result.errors?.[0],
      };
    } catch (error) {
      return {
        result: null,
        error: "Error adding renter's device: " + error.message,
      };
    }
  }

  async function createRenterAndAddDevice(deviceRef, renterInput) {
    const device = unref(deviceRef);
    if (!apolloClient.value || !currentFacility.value?.code || !device?.id)
      return;

    try {
      const result = await apolloClient.value.mutate({
        mutation: CREATE_FACILITY_RENTER,
        variables: {
          deviceId: device.id,
          facilityCode: currentFacility.value?.code,
          firstName: renterInput.firstName,
          lastName: renterInput.lastName,
          primaryPhone: renterInput.primaryPhone,
        },
      });
      // No need to refetch, the cache will be updated automatically
      return {
        result: result.data?.createFacilityRenter,
        error: result.errors?.[0],
      };
    } catch (error) {
      return {
        result: null,
        error: "Error creating renter and adding device: " + error.message,
      };
    }
  }

  async function removeRenterDevice(deviceRef) {
    const device = unref(deviceRef);
    if (!apolloClient.value || !currentFacility.value?.code || !device?.id)
      return;

    try {
      const result = await apolloClient.value.mutate({
        mutation: REMOVE_FACILITY_DEVICE_RENTER,
        variables: {
          deviceId: device.id,
          facilityCode: currentFacility.value?.code,
        },
      });
      // No need to refetch, the cache will be updated automatically
      // if (result?.data?.removeFacilityDeviceRenter) {
      //   observableQuery?.refetch();
      // }
      return {
        result: result.data?.removeFacilityDeviceRenter,
        error: result.errors?.[0],
      };
    } catch (error) {
      return {
        result: null,
        error: "Error removing renter's device: " + error.message,
      };
    }
  }

  return {
    devices,
    facilityDevicesLoading,
    devicesWithRenters,
    devicesWithoutRenters,
    setApolloClient,
    fetchFacilityDevices,
    fetchFacilityRenters,
    addRenterDevice,
    createRenterAndAddDevice,
    removeRenterDevice,
  };
}
